import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import FlagToggle from '../components/FlagToggle';

const SAFE_NEXT = /^\/(?!\/)[a-zA-Z0-9/_?=&-]*$/;
// LeadWing separation (2026-02-10): the operator cohort experiment was
// retired along with the /operator and /operator/leads routes. Only /app
// remains as a valid non-admin post-login destination. Any legacy backend
// cohort value pointing at /operator falls through to the /app fallback.
// Post-Signup Immigration Activation Fix (2026-02-20): the allowlist
// now includes /mos-precheck so that a deep-linked login (e.g. from
// /mos-precheck → /login?next=/mos-precheck) resumes correctly.
// Returning-user default remains /app to preserve conversation history
// UX for existing accounts.
const ALLOWED_BACKEND_ROUTES = new Set(['/mos-precheck', '/app']);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next');
  const explicitNext = rawNext && SAFE_NEXT.test(rawNext) ? rawNext : null;
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Resolution order:
      //   1. Explicit ?next=… on the URL (always wins for deep-links).
      //   2. Admins land directly on the analytics dashboard.
      //   3. Backend-driven cohort default (`next_route`), validated against
      //      the allowlist.
      //   4. Hardcoded /app fallback.
      let destination = '/app';
      if (explicitNext) {
        destination = explicitNext;
      } else if (result.role === 'admin') {
        destination = '/analytics';
      } else if (result.nextRoute && ALLOWED_BACKEND_ROUTES.has(result.nextRoute)) {
        destination = result.nextRoute;
      }
      navigate(destination);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="hussar-auth-page">
      <div className="hussar-auth-toolbar">
        <FlagToggle />
      </div>
      <div className="hussar-auth-container">
        <div className="hussar-auth-header">
          <h1 className="hussar-auth-title">{t('auth.loginTitle')}</h1>
          <p className="hussar-auth-trust">
            <Lock className="w-3 h-3" />
            <span>{t('auth.trustSecurity')}</span>
          </p>
        </div>

        <Card className="hussar-auth-card">
          <form onSubmit={handleSubmit} className="hussar-auth-form">
            {error && (
              <div className="hussar-auth-error">
                {error}
              </div>
            )}

            <div className="hussar-form-group">
              <label className="hussar-form-label">
                {t('auth.labelEmail')}
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.placeholderEmail')}
                required
                className="hussar-form-input"
              />
            </div>

            <div className="hussar-form-group">
              <label className="hussar-form-label">
                {t('auth.labelPassword')}
              </label>
              <div className="hussar-password-wrap">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.placeholderPassword')}
                  required
                  className="hussar-form-input hussar-form-input-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="hussar-password-toggle"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                  tabIndex={0}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="hussar-btn-primary hussar-btn-full hussar-btn-auth"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.loadingLogin')}
                </>
              ) : (
                <>
                  {t('auth.submitLogin')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </form>
        </Card>

        <p className="hussar-auth-switch">
          {t('auth.noAccount')}{' '}
          <Link to={`/register?next=${encodeURIComponent(explicitNext || '/app')}`} className="hussar-auth-link">
            {t('auth.switchToRegister')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
